import React, { useState, useEffect } from "react";
import axios from "axios";
import MaterialGroupSelect from "../MaterialGroupSelect";
import MaterialLocationSelect from "../MaterialLocationSelect";
import { toast } from "react-toastify";
import LoadingOverlay from "../LoadingOverlay";
import ConfirmDialog from "../ConfirmDialog";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";

const LIST_UNIT = ["ROLL", "SHEET", "PLY", "KG", "PCS", "RIM"];

function MaterialListModal({ dataSet, onClose, refreshData }) {
  const [selectedMatGroup, setSelectedMatGroup] = useState(null);
  const [productName, setProductName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [productUnit, setProductUnit] = useState(LIST_UNIT[0]);
  const [productLocation, setProductLocation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);

  useEffect(() => {
    if (dataSet) {
      setProductName(dataSet.product_name || "");
      setProductCode(dataSet.product_code || "");
      setProductUnit(dataSet.unit || LIST_UNIT[0]);
    }
  }, [dataSet]);

  const updateOrder = async () => {
    setIsLoading(true);
    try {
      const res = await axios.patch(
        "https://eps.elkomprintama.com/patch/api/store/store/mats",
        {
          productCode: productCode.toUpperCase(),
          productName: productName.toUpperCase(),
          groupId: selectedMatGroup,
          unit: productUnit,
          productLocationId: productLocation,
          productId: dataSet.product_id,
        },
      );
      if (res.status === 200) {
        toast.success(res.data.msg);
        onClose();
        refreshData();
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Update failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productName || !productCode || !selectedMatGroup) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg relative overflow-hidden">
      {isLoading && <LoadingOverlay message="Updating…" />}

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-bold text-gray-800">Edit Material</h2>
          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
            {dataSet?.product_name}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <MaterialLocationSelect
          dataSet={dataSet}
          onChangeList={setProductLocation}
        />

        {/* Name + Code */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-4">
            <label className={lbl}>Product Name</label>
            <input
              className={inp}
              type="text"
              placeholder="Product Name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
          </div>
          <div>
            <label className={lbl}>Code</label>
            <input
              className={inp}
              type="text"
              placeholder="Code"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
            />
          </div>
        </div>

        <MaterialGroupSelect
          dataSet={dataSet}
          onChangeList={setSelectedMatGroup}
        />

        <div>
          <label className={lbl}>Unit</label>
          <select
            className={inp}
            value={productUnit}
            onChange={(e) => setProductUnit(e.target.value)}
          >
            {LIST_UNIT.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Update
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
          >
            Close
          </button>
        </div>
      </form>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Update this material?"
          onConfirm={() => {
            setShowConfirm(false);
            updateOrder();
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
}

export default MaterialListModal;
